import { create } from "zustand";
import { useShallow } from "zustand/shallow";
import { streamCoachMessage, type CoachRequest } from "../services/coachStream";

export interface CoachMessage {
  role: "user" | "assistant";
  content: string;
  timestamp: string;
}

type CoachMode = "chat" | "essay_coach" | "counselor" | "assessment" | "admissions";

interface CoachState {
  isOpen: boolean;
  mode: CoachMode;
  workflow: string | null;
  essayId: string | null;
  sessionId: string | null;
  messages: CoachMessage[];
  isStreaming: boolean;
  streamingText: string;
  monthlyUsed: number;
  monthlyLimit: number;
  error: string | null;

  open: () => void;
  close: () => void;
  toggle: () => void;
  setMode: (mode: CoachMode, opts?: { workflow?: string; essayId?: string }) => void;
  sendMessage: (text: string) => void;
  newSession: () => void;
  clearError: () => void;
}

let activeController: AbortController | null = null;

export const useCoachStore = create<CoachState>()((set, get) => ({
  isOpen: false,
  mode: "chat",
  workflow: null,
  essayId: null,
  sessionId: null,
  messages: [],
  isStreaming: false,
  streamingText: "",
  monthlyUsed: 0,
  monthlyLimit: 500,
  error: null,

  open: () => set({ isOpen: true }),
  close: () => {
    if (activeController) { activeController.abort(); activeController = null; }
    set({ isOpen: false, isStreaming: false });
  },
  toggle: () => set((s) => ({ isOpen: !s.isOpen })),

  setMode: (mode, opts) => set({
    mode,
    workflow: opts?.workflow || null,
    essayId: opts?.essayId || null,
    sessionId: null,
    messages: [],
    streamingText: "",
    error: null,
  }),

  newSession: () => set({ sessionId: null, messages: [], streamingText: "", error: null }),

  clearError: () => set({ error: null }),

  sendMessage: (text: string) => {
    const state = get();
    if (state.isStreaming || !text.trim()) return;

    // Add user message
    const userMsg: CoachMessage = { role: "user", content: text.trim(), timestamp: new Date().toISOString() };
    set((s) => ({
      messages: [...s.messages, userMsg],
      isStreaming: true,
      streamingText: "",
      error: null,
    }));

    const body: CoachRequest = {
      sessionId: state.sessionId,
      mode: state.mode,
      message: text.trim(),
      workflow: state.workflow || undefined,
      essayId: state.essayId || undefined,
      pageContext: typeof window !== "undefined" ? window.location.pathname : undefined,
    };

    activeController = streamCoachMessage(body, {
      onToken: (token) => {
        set((s) => ({ streamingText: s.streamingText + token }));
      },
      onDone: (data) => {
        const finalText = get().streamingText;
        set((s) => ({
          messages: [...s.messages, {
            role: "assistant" as const,
            content: finalText,
            timestamp: new Date().toISOString(),
          }],
          sessionId: data.sessionId,
          isStreaming: false,
          streamingText: "",
          monthlyUsed: s.monthlyUsed + 1,
        }));
        activeController = null;
      },
      onError: (error, code) => {
        set({
          isStreaming: false,
          streamingText: "",
          error: code === "MONTHLY_LIMIT"
            ? "Monthly message limit reached. Upgrade your plan for more."
            : code === "BURST_LIMIT"
              ? "Too many messages. Wait a moment and try again."
              : error,
        });
        activeController = null;
      },
    });
  },
}));

export function useCoach() {
  return useCoachStore(useShallow((s) => ({
    isOpen: s.isOpen,
    mode: s.mode,
    messages: s.messages,
    isStreaming: s.isStreaming,
    streamingText: s.streamingText,
    error: s.error,
    monthlyUsed: s.monthlyUsed,
    monthlyLimit: s.monthlyLimit,
    open: s.open,
    close: s.close,
    toggle: s.toggle,
    setMode: s.setMode,
    sendMessage: s.sendMessage,
    newSession: s.newSession,
    clearError: s.clearError,
  })));
}
